"use client"

import type { Tokens } from "@mux/playback-core"
import { UserId } from "@researchwiseai/identities"
import mixpanel from "mixpanel-browser"
import Image from "next/image"
import Video from "next-video"
import type { Asset } from "next-video/dist/assets.js"
import { SyntheticEvent, useRef } from "react"
import { useEventBusListener } from "lib/events"
import { pushGtmEvent } from "lib/gtm"

interface Props {
    src: Asset
    tokens?: Tokens | undefined
    metadata: {
        title: string
        slug: string
        free: boolean
        userId?: string
    }
    chapters?: { startTime: number; value: string }[] | undefined | null
    poster?: {
        url?: string | null
        blurDataUrl?: string | null
    }
}

export function VideoWrapper({ src, chapters, tokens, metadata, poster }: Props) {
    const userId = metadata.userId ? UserId.fromUrl(metadata.userId) : null
    const ref = useRef<HTMLVideoElement>(null)

    useEventBusListener("academy-video-seek", (time: number) => {
        if (ref.current) {
            ref.current.currentTime = time
            ref.current.play()
        }
    })

    const onPlay = () => {
        mixpanel.track("Academy: Video play", {
            title: metadata.title,
            slug: metadata.slug,
            free: metadata.free,
            authenticated: tokens !== undefined,
            ...(userId && { distinct_id: userId.id }),
        })

        pushGtmEvent({
            event: "academy_video_play",
            video_title: metadata.title,
            video_slug: metadata.slug,
            free: metadata.free,
            authenticated: tokens !== undefined,
        })
    }

    function addChapters({ target }: SyntheticEvent<HTMLVideoElement, Event>) {
        // @ts-expect-error addChapters is not part of the HTMLVideoElement type
        typeof target.addChapters === "function" &&
            chapters != null &&
            // @ts-expect-error addChapters is not part of the HTMLVideoElement type
            target.addChapters(chapters)
    }

    return (
        <Video
            src={src}
            ref={ref}
            onPlay={onPlay}
            playbackToken={tokens?.playback}
            tokens={tokens}
            onLoadedMetadata={addChapters}
            metadata={{
                video_id: metadata.slug,
                video_title: metadata.title,
                ...(metadata.userId && { viewer_user_id: metadata.userId }),
            }}
        >
            {poster?.url && (
                <Image
                    src={poster.url}
                    fill
                    slot="poster"
                    sizes="(max-width: 1024px) 100vw, 66vw"
                    placeholder={poster.blurDataUrl ? "blur" : undefined}
                    blurDataURL={poster.blurDataUrl ?? undefined}
                    alt={`Poster image for ${metadata.title}`}
                    priority
                    className="object-cover"
                />
            )}
        </Video>
    )
}
